import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/app/components/ui/button";
import {
  acceptAllConsent,
  COOKIE_CONSENT_EVENT,
  COOKIE_PREFERENCES_OPEN_EVENT,
  essentialConsent,
  readCookieConsent,
  rejectConsent,
  writeCookieConsent,
  type CookieConsentChoice,
  type CookieConsentTier,
} from "@/lib/cookieConsent";

const outlineBtnClass =
  "w-full sm:flex-1 lg:flex-none min-h-10 border-slate-500 bg-slate-900 text-slate-100 hover:bg-slate-800 hover:text-slate-100";

function tierLabel(tier: CookieConsentTier): string {
  switch (tier) {
    case "reject":
      return "Reject optional measurement";
    case "essential":
      return "Essential + measurement";
    case "accept_all":
      return "Accept all";
    default: {
      const _exhaustive: never = tier;
      return _exhaustive;
    }
  }
}

function persistTier(tier: CookieConsentTier): CookieConsentChoice {
  switch (tier) {
    case "reject":
      return rejectConsent();
    case "essential":
      return essentialConsent();
    case "accept_all":
      return acceptAllConsent();
    default: {
      const _exhaustive: never = tier;
      return _exhaustive;
    }
  }
}

function ConsentActions({
  onChoose,
  layout = "banner",
}: {
  onChoose: (tier: CookieConsentTier) => void;
  layout?: "banner" | "dialog";
}) {
  const stack = layout === "banner" ? "flex-col sm:flex-row" : "flex-col";
  return (
    <div className={`flex ${stack} flex-wrap gap-2 sm:gap-3 shrink-0 w-full lg:w-auto`}>
      <Button type="button" variant="outline" className={outlineBtnClass} onClick={() => onChoose("reject")}>
        Reject optional measurement
      </Button>
      <Button type="button" variant="outline" className={outlineBtnClass} onClick={() => onChoose("essential")}>
        Essential + measurement
      </Button>
      <Button
        type="button"
        className="w-full sm:flex-1 lg:flex-none min-h-10 bg-teal-600 text-white hover:bg-teal-500"
        onClick={() => onChoose("accept_all")}
      >
        Accept all
      </Button>
    </div>
  );
}

/**
 * Bottom banner when no saved choice. GA4 and Clarity load only after Essential or Accept all.
 */
export function CookieConsent() {
  const [choice, setChoice] = useState<CookieConsentChoice | null>(() => readCookieConsent());

  useEffect(() => {
    const onUpdate = () => setChoice(readCookieConsent());
    window.addEventListener(COOKIE_CONSENT_EVENT, onUpdate);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, onUpdate);
  }, []);

  const persist = useCallback((next: CookieConsentChoice) => {
    writeCookieConsent(next);
    setChoice(next);
  }, []);

  if (choice) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-slate-700/80 bg-slate-950/95 backdrop-blur-md shadow-[0_-8px_32px_rgba(7,26,33,0.14)]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
          <div className="min-w-0 flex-1 max-w-2xl">
            <p id="cookie-consent-title" className="text-slate-100 text-sm font-semibold mb-1">
              Cookies on deskreplay.com
            </p>
            <p id="cookie-consent-desc" className="text-on-dark-muted text-sm leading-relaxed">
              We use strictly necessary storage (including a CAPTCHA on pilot signup). Google Analytics and Microsoft
              Clarity run only if you choose Essential + measurement or Accept all. Reject optional measurement turns
              those tools off; core pages and forms still work, but we cannot measure or improve the site from your
              visit. We ask once per browser unless you change preferences in the footer.{" "}
              <Link
                to="/legal/cookie-policy"
                className="text-teal-400 hover:text-teal-300 underline-offset-2 hover:underline"
              >
                Cookie policy
              </Link>
            </p>
          </div>

          <ConsentActions onChoose={(tier) => persist(persistTier(tier))} layout="banner" />
        </div>
      </div>
    </div>
  );
}

/** Change cookie choice anytime (footer). */
export function CookiePreferencesDialog() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<CookieConsentChoice | null>(() => readCookieConsent());

  useEffect(() => {
    const onOpen = () => {
      setCurrent(readCookieConsent());
      setOpen(true);
    };
    const onConsent = () => setCurrent(readCookieConsent());
    window.addEventListener(COOKIE_PREFERENCES_OPEN_EVENT, onOpen);
    window.addEventListener(COOKIE_CONSENT_EVENT, onConsent);
    return () => {
      window.removeEventListener(COOKIE_PREFERENCES_OPEN_EVENT, onOpen);
      window.removeEventListener(COOKIE_CONSENT_EVENT, onConsent);
    };
  }, []);

  const save = useCallback((tier: CookieConsentTier) => {
    writeCookieConsent(persistTier(tier));
    setOpen(false);
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-labelledby="cookie-prefs-title"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-lg border border-slate-700 bg-slate-950 p-5 shadow-xl">
        <h2 id="cookie-prefs-title" className="text-slate-100 text-base font-semibold mb-2">
          Cookie preferences
        </h2>
        <p className="text-on-dark-muted text-sm leading-relaxed mb-4">
          {current
            ? `Current choice: ${tierLabel(current.tier)}.`
            : "You have not saved a choice yet."}{" "}
          Reject turns off Google Analytics and Microsoft Clarity. Essential + measurement and Accept all enable them.{" "}
          <Link to="/legal/cookie-policy" className="text-teal-400 hover:text-teal-300 underline-offset-2 hover:underline">
            Cookie policy
          </Link>
        </p>
        <ConsentActions onChoose={save} layout="dialog" />
        <Button
          type="button"
          variant="ghost"
          className="mt-3 w-full text-slate-400 hover:text-slate-200"
          onClick={() => setOpen(false)}
        >
          Close
        </Button>
      </div>
    </div>
  );
}

export function CookiePreferencesLink() {
  return (
    <button
      type="button"
      className="text-on-dark-muted hover:text-teal-400 transition-colors bg-transparent border-0 p-0 cursor-pointer text-xs"
      onClick={() => window.dispatchEvent(new CustomEvent(COOKIE_PREFERENCES_OPEN_EVENT))}
    >
      Cookie preferences
    </button>
  );
}
